import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProjectGantt from "@/components/ProjectGantt";
import AssignmentsMatrix from "@/components/AssignmentsMatrix";
import ProjectAssignmentsSheet from "@/components/ProjectAssignmentsSheet";
import SearchableSelect from "@/components/SearchableSelect";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, UsersThree, MagnifyingGlass, Archive, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyProj = {
    customer_id: "",
    name: "",
    project_code: "",
    billable: true,
    active: true,
    is_special: false,
    special_kind: null,
    project_manager_id: null,
    planned_hours_by_group: {},
    date_start: "",
    date_end: "",
    date_adjusted: "",
};

export default function AdminProjects() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyProj);
    const [yearFilter, setYearFilter] = useState("all");
    const [customerFilter, setCustomerFilter] = useState("");
    const [activeFilter, setActiveFilter] = useState("active");
    const [search, setSearch] = useState("");
    const [asgSheet, setAsgSheet] = useState({ open: false, project: null });
    const [confirmArchive, setConfirmArchive] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [importingDates, setImportingDates] = useState(false);

    const handleImportDates = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setImportingDates(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await http.post("/projects/import-dates", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(
                `Fechas importadas: ${r.data.updated} proyectos actualizados (${r.data.matched} coincidencias, ${r.data.unmatched} sin asociar).`,
            );
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al importar fechas");
        } finally {
            setImportingDates(false);
        }
    };

    const runArchiveNoHours = async () => {
        setArchiving(true);
        try {
            const r = await http.post("/admin/projects/archive-no-hours-2026");
            toast.success(
                `${r.data.archived} proyectos archivados. ${r.data.remaining_active} siguen activos.`,
            );
            setConfirmArchive(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al archivar");
        } finally {
            setArchiving(false);
        }
    };

    const load = async () => {
        const [p, c, u, g] = await Promise.all([
            http.get("/projects", { params: { include_archived: true } }),
            http.get("/customers"),
            http.get("/users"),
            http.get("/groups"),
        ]);
        setProjects(p.data);
        setCustomers(c.data);
        setUsers(u.data);
        setGroups(g.data);
    };
    useEffect(() => {
        load();
    }, []);

    const projectYear = (code) => {
        if (!code || code.length < 4) return null;
        const head = code.slice(0, 4);
        if (/^\d{4}$/.test(head)) {
            const y = parseInt(head, 10);
            if (y >= 1990 && y <= 2100) return y;
        }
        return null;
    };

    const availableYears = useMemo(() => {
        const s = new Set();
        for (const p of projects) {
            const y = projectYear(p.project_code);
            if (y) s.add(y);
        }
        return Array.from(s).sort((a, b) => b - a);
    }, [projects]);

    const customerName = (id) =>
        customers.find((c) => c.customer_id === id)?.name || "—";

    const managerName = (id) => users.find((u) => u.user_id === id)?.name;

    const filteredProjects = useMemo(() => {
        const q = search.trim().toLowerCase();
        return projects.filter((p) => {
            if (activeFilter === "active" && !p.active) return false;
            if (activeFilter === "inactive" && p.active) return false;
            if (customerFilter && p.customer_id !== customerFilter) return false;
            if (yearFilter !== "all") {
                const y = projectYear(p.project_code);
                if (String(y) !== yearFilter) return false;
            }
            if (q) {
                const cust = customerName(p.customer_id).toLowerCase();
                const hay =
                    p.name.toLowerCase().includes(q) ||
                    (p.project_code || "").toLowerCase().includes(q) ||
                    cust.includes(q);
                if (!hay) return false;
            }
            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, yearFilter, customerFilter, activeFilter, search, customers]);

    // Sortable columns
    const accessors = useMemo(
        () => ({
            name: (p) => p.name || "",
            customer: (p) => customerName(p.customer_id),
            code: (p) => p.project_code || "",
            year: (p) => projectYear(p.project_code) || 0,
            manager: (p) => managerName(p.project_manager_id) || "",
            planned: (p) => Number(p.planned_hours || 0),
            budget: (p) => Number(p.planned_budget || 0),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [customers, users],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        filteredProjects,
        "admin-projects",
        accessors,
        { initialKey: "name", initialDir: "asc" },
    );

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            if (payload.project_manager_id === "_none") payload.project_manager_id = null;
            // Sanitize breakdown → only positive numeric values
            const clean = {};
            for (const [gid, hh] of Object.entries(payload.planned_hours_by_group || {})) {
                const n = Number(hh);
                if (!Number.isNaN(n) && n > 0) clean[gid] = n;
            }
            payload.planned_hours_by_group = clean;
            delete payload.planned_hours; // computed server-side
            if (form.project_id) {
                await http.put(`/projects/${form.project_id}`, payload);
                toast.success("Proyecto actualizado");
            } else {
                await http.post("/projects", payload);
                toast.success("Proyecto creado");
            }
            setOpen(false);
            setForm(emptyProj);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`¿Archivar proyecto "${p.name}"?`)) return;
        await http.delete(`/projects/${p.project_id}`);
        load();
    };

    const reactivate = async (p) => {
        await http.put(`/projects/${p.project_id}`, {
            ...p,
            planned_hours_by_group: p.planned_hours_by_group || {},
            active: true,
        });
        toast.success("Proyecto reactivado");
        load();
    };

    const managerOptions = users.filter((u) => u.role === "admin" || u.role === "pm");

    const formTotal = useMemo(
        () =>
            Object.values(form.planned_hours_by_group || {}).reduce(
                (a, b) => a + (Number(b) || 0),
                0,
            ),
        [form.planned_hours_by_group],
    );

    const formBudget = useMemo(() => {
        const map = form.planned_hours_by_group || {};
        let total = 0;
        for (const g of groups) {
            const h = Number(map[g.group_id] || 0);
            if (h > 0) total += h * Number(g.hourly_rate || 0);
        }
        return total;
    }, [form.planned_hours_by_group, groups]);

    return (
        <div data-testid="page-admin-projects">
            <PageHeader
                eyebrow="Administración"
                title="Proyectos"
                description="Crea, edita y archiva proyectos. La asignación de personas se hace desde cada proyecto."
                actions={
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button
                                onClick={() => setForm(emptyProj)}
                                className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                data-testid="project-new-btn"
                            >
                                <Plus size={14} className="mr-2" /> Nuevo proyecto
                            </Button>
                        </SheetTrigger>
                        <SheetContent className="rounded-none sm:max-w-md overflow-y-auto">
                            <SheetHeader>
                                <SheetTitle className="font-display">
                                    {form.project_id ? "Editar proyecto" : "Nuevo proyecto"}
                                </SheetTitle>
                                <SheetDescription className="text-xs text-slate-500">
                                    Define cliente, código, manager y las horas planificadas por
                                    grupo de tarifa.
                                </SheetDescription>
                            </SheetHeader>
                            <form onSubmit={submit} className="mt-6 space-y-3">
                                <div>
                                    <Label className="text-xs">Cliente</Label>
                                    <SearchableSelect
                                        value={form.customer_id}
                                        onChange={(v) =>
                                            setForm({ ...form, customer_id: v })
                                        }
                                        options={customers.map((c) => ({
                                            value: c.customer_id,
                                            label: c.name,
                                            hint: c.code || "",
                                        }))}
                                        placeholder="Selecciona un cliente"
                                        allowClear={false}
                                        className="mt-1"
                                        data-testid="project-customer"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Nombre</Label>
                                    <Input
                                        required
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm({ ...form, name: e.target.value })
                                        }
                                        className="mt-1 rounded-sm border-slate-300"
                                        data-testid="project-name"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Código</Label>
                                    <Input
                                        value={form.project_code || ""}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                project_code: e.target.value,
                                            })
                                        }
                                        className="mt-1 rounded-sm border-slate-300 font-mono"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Manager del proyecto</Label>
                                    <SearchableSelect
                                        value={form.project_manager_id || ""}
                                        onChange={(v) =>
                                            setForm({
                                                ...form,
                                                project_manager_id: v || null,
                                            })
                                        }
                                        options={managerOptions.map((u) => ({
                                            value: u.user_id,
                                            label: u.name,
                                            hint: u.role === "admin" ? "Admin" : "PM",
                                        }))}
                                        placeholder="Sin manager"
                                        className="mt-1"
                                        data-testid="project-manager"
                                    />
                                    <div className="text-[11px] text-slate-500 mt-1">
                                        El manager recibe y aprueba las solicitudes de
                                        asignación a este proyecto.
                                    </div>
                                </div>
                                <PlannedByGroupEditor
                                    groups={groups}
                                    value={form.planned_hours_by_group || {}}
                                    total={formTotal}
                                    budget={formBudget}
                                    onChange={(next) =>
                                        setForm({
                                            ...form,
                                            planned_hours_by_group: next,
                                        })
                                    }
                                />
                                <div className="flex items-center gap-4 pt-1">
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="billable"
                                            checked={form.billable}
                                            onCheckedChange={(v) =>
                                                setForm({ ...form, billable: !!v })
                                            }
                                        />
                                        <Label
                                            htmlFor="billable"
                                            className="text-xs cursor-pointer"
                                        >
                                            Facturable
                                        </Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="active"
                                            checked={form.active}
                                            onCheckedChange={(v) =>
                                                setForm({ ...form, active: !!v })
                                            }
                                        />
                                        <Label
                                            htmlFor="active"
                                            className="text-xs cursor-pointer"
                                        >
                                            Activo
                                        </Label>
                                    </div>
                                </div>
                                <div className="pt-1">
                                    <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        Fechas del proyecto
                                    </Label>
                                    <div className="grid grid-cols-3 gap-2 mt-1.5">
                                        <div>
                                            <Label className="text-[10px] text-slate-500">
                                                Inicio
                                            </Label>
                                            <Input
                                                type="date"
                                                value={form.date_start || ""}
                                                onChange={(e) =>
                                                    setForm({ ...form, date_start: e.target.value })
                                                }
                                                className="rounded-sm border-slate-300 text-xs"
                                                data-testid="project-date-start"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-[10px] text-slate-500">
                                                Fin
                                            </Label>
                                            <Input
                                                type="date"
                                                value={form.date_end || ""}
                                                onChange={(e) =>
                                                    setForm({ ...form, date_end: e.target.value })
                                                }
                                                className="rounded-sm border-slate-300 text-xs"
                                                data-testid="project-date-end"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-[10px] text-slate-500">
                                                Ajustada
                                            </Label>
                                            <Input
                                                type="date"
                                                value={form.date_adjusted || ""}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        date_adjusted: e.target.value,
                                                    })
                                                }
                                                className="rounded-sm border-slate-300 text-xs"
                                                data-testid="project-date-adjusted"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-1">
                                        "Ajustada" = nueva fecha de fin si hay retrasos (se muestra
                                        como extensión en el Gantt).
                                    </p>
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                    data-testid="project-save"
                                >
                                    {form.project_id ? "Guardar" : "Crear"}
                                </Button>
                            </form>
                        </SheetContent>
                    </Sheet>
                }
            />
            <div className="p-6 lg:p-10">
                <Tabs defaultValue="list">
                    <TabsList className="mb-5">
                        <TabsTrigger value="list" data-testid="tab-projects-list">
                            Proyectos
                        </TabsTrigger>
                        <TabsTrigger value="gantt" data-testid="tab-projects-gantt">
                            Planificación (Gantt)
                        </TabsTrigger>
                        <TabsTrigger value="matrix" data-testid="tab-projects-matrix">
                            Asignaciones
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="list">
                <div className="flex flex-wrap items-center gap-3 mb-4 border border-slate-200 bg-white p-3">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mr-2">
                        Filtros
                    </span>
                    <div className="relative flex-1 min-w-[220px] max-w-xs">
                        <MagnifyingGlass
                            size={14}
                            className="absolute left-2 top-2.5 text-slate-400"
                        />
                        <Input
                            placeholder="Buscar por nombre, código o cliente..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="rounded-sm border-slate-300 pl-7 h-8"
                            data-testid="proj-search"
                        />
                    </div>
                    <Select value={yearFilter} onValueChange={setYearFilter}>
                        <SelectTrigger
                            className="rounded-sm border-slate-300 w-32 h-8"
                            data-testid="proj-year-filter"
                        >
                            <SelectValue placeholder="Anualidad" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos los años</SelectItem>
                            {availableYears.map((y) => (
                                <SelectItem key={y} value={String(y)}>
                                    {y}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="w-56">
                        <SearchableSelect
                            value={customerFilter}
                            onChange={(v) => setCustomerFilter(v)}
                            options={customers.map((c) => ({
                                value: c.customer_id,
                                label: c.name,
                                hint: c.code || "",
                            }))}
                            placeholder="Todos los clientes"
                            className="!py-1.5 h-8"
                            data-testid="proj-customer-filter"
                        />
                    </div>
                    <Select value={activeFilter} onValueChange={setActiveFilter}>
                        <SelectTrigger
                            className="rounded-sm border-slate-300 w-32 h-8"
                            data-testid="proj-active-filter"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="active">Activos</SelectItem>
                            <SelectItem value="inactive">Archivados</SelectItem>
                            <SelectItem value="all">Todos</SelectItem>
                        </SelectContent>
                    </Select>
                    <span className="text-xs text-slate-500 ml-auto font-mono">
                        {sorted.length} de {projects.length}
                    </span>
                    <Button
                        variant="outline"
                        onClick={() => setConfirmArchive(true)}
                        className="rounded-sm border-amber-300 text-amber-800 hover:bg-amber-50 h-8"
                        data-testid="archive-no-hours-btn"
                    >
                        <Archive size={14} className="mr-2" /> Archivar sin horas 2026
                    </Button>
                </div>

                <div className="border border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm" data-testid="projects-table">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <th {...headerProps("name", "py-3 px-4 text-left")}>
                                    Proyecto
                                    <SortIndicator active={sortKey === "name"} dir={sortDir} />
                                </th>
                                <th {...headerProps("customer", "py-3 px-4 text-left")}>
                                    Cliente
                                    <SortIndicator
                                        active={sortKey === "customer"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("code", "py-3 px-4 text-left")}>
                                    Código
                                    <SortIndicator active={sortKey === "code"} dir={sortDir} />
                                </th>
                                <th {...headerProps("year", "py-3 px-4 text-left")}>
                                    Año
                                    <SortIndicator active={sortKey === "year"} dir={sortDir} />
                                </th>
                                <th {...headerProps("manager", "py-3 px-4 text-left")}>
                                    Manager
                                    <SortIndicator
                                        active={sortKey === "manager"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("planned", "py-3 px-4 text-right")}>
                                    H. planif.
                                    <SortIndicator
                                        active={sortKey === "planned"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("budget", "py-3 px-4 text-right")}>
                                    Presupuesto
                                    <SortIndicator
                                        active={sortKey === "budget"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th className="py-3 px-4 text-left">Tipo</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((p) => (
                                <tr
                                    key={p.project_id}
                                    className={`border-b border-slate-100 hover:bg-slate-50 ${!p.active ? "opacity-60" : ""}`}
                                    data-testid={`project-row-${p.project_id}`}
                                >
                                    <td className="py-3 px-4 font-medium">
                                        <button
                                            onClick={() =>
                                                navigate(`/admin/projects/${p.project_id}`)
                                            }
                                            className="text-left text-[#002BAF] hover:underline"
                                            title="Ver ficha del proyecto"
                                            data-testid={`project-open-${p.project_id}`}
                                        >
                                            {p.name}
                                        </button>
                                    </td>
                                    <td className="py-3 px-4 text-slate-700">
                                        {customerName(p.customer_id)}
                                    </td>
                                    <td className="py-3 px-4 font-mono text-slate-600">
                                        {p.project_code || "—"}
                                    </td>
                                    <td className="py-3 px-4 font-mono text-slate-700">
                                        {projectYear(p.project_code) || (
                                            <span className="text-slate-400">—</span>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-slate-700 text-xs">
                                        {managerName(p.project_manager_id) || (
                                            <span className="text-slate-400">—</span>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono tabular-nums">
                                        {Number(p.planned_hours || 0).toLocaleString("es-ES", {
                                            maximumFractionDigits: 1,
                                        })}
                                    </td>
                                    <td
                                        className="py-3 px-4 text-right font-mono tabular-nums text-slate-800"
                                        data-testid={`project-budget-${p.project_id}`}
                                    >
                                        {p.planned_budget
                                            ? Number(p.planned_budget).toLocaleString(
                                                  "es-ES",
                                                  {
                                                      minimumFractionDigits: 2,
                                                      maximumFractionDigits: 2,
                                                  },
                                              ) + " €"
                                            : "—"}
                                    </td>
                                    <td className="py-3 px-4">
                                        {!p.active ? (
                                            <span className="px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wider bg-slate-200 text-slate-600">
                                                Archivado
                                            </span>
                                        ) : p.is_special ? (
                                            <span className="px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800">
                                                Especial
                                            </span>
                                        ) : p.billable ? (
                                            <span className="px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                                                Facturable
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-700">
                                                Interno
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-right whitespace-nowrap">
                                        {p.active && !p.is_special && (
                                            <button
                                                onClick={() =>
                                                    setAsgSheet({ open: true, project: p })
                                                }
                                                title="Gestionar asignaciones"
                                                className="text-slate-500 hover:text-[#002BAF] mr-3"
                                                data-testid={`project-assignments-${p.project_id}`}
                                            >
                                                <UsersThree size={16} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                setForm({
                                                    ...emptyProj,
                                                    ...p,
                                                    planned_hours_by_group:
                                                        p.planned_hours_by_group || {},
                                                    project_manager_id:
                                                        p.project_manager_id || null,
                                                });
                                                setOpen(true);
                                            }}
                                            className="text-slate-500 hover:text-slate-900 mr-3"
                                            data-testid={`project-edit-${p.project_id}`}
                                        >
                                            <PencilSimple size={16} />
                                        </button>
                                        {p.active ? (
                                            <button
                                                onClick={() => remove(p)}
                                                title="Archivar"
                                                className="text-slate-400 hover:text-red-600"
                                                data-testid={`project-archive-${p.project_id}`}
                                            >
                                                <Trash size={16} />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => reactivate(p)}
                                                className="text-emerald-700 hover:text-emerald-900 text-xs underline"
                                                data-testid={`project-reactivate-${p.project_id}`}
                                            >
                                                Reactivar
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <ProjectAssignmentsSheet
                    open={asgSheet.open}
                    project={asgSheet.project}
                    onOpenChange={(v) =>
                        setAsgSheet((prev) => ({ ...prev, open: v }))
                    }
                    onChanged={load}
                />

                <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
                    <AlertDialogContent data-testid="archive-no-hours-dialog">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Archivar proyectos sin horas en 2026</AlertDialogTitle>
                            <AlertDialogDescription>
                                Se archivarán todos los proyectos activos que{" "}
                                <strong>no tengan horas imputadas en 2026</strong>. Se{" "}
                                <strong>excluyen</strong> los proyectos especiales
                                (vacaciones, formación, bajas) y los internos (cliente
                                Ingartek / código ING_).
                                <br />
                                <span className="text-slate-500">
                                    Es reversible: podrás reactivar cualquier proyecto desde el
                                    botón "Reactivar".
                                </span>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel
                                disabled={archiving}
                                data-testid="archive-no-hours-cancel"
                            >
                                Cancelar
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={(ev) => {
                                    ev.preventDefault();
                                    runArchiveNoHours();
                                }}
                                disabled={archiving}
                                className="bg-amber-600 hover:bg-amber-700"
                                data-testid="archive-no-hours-confirm"
                            >
                                {archiving ? "Archivando…" : "Archivar ahora"}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
                    </TabsContent>
                    <TabsContent value="gantt">
                        <div className="mb-4 flex items-center justify-end">
                            <input
                                type="file"
                                accept=".xlsx"
                                id="import-dates-input"
                                className="hidden"
                                onChange={handleImportDates}
                                data-testid="import-dates-input"
                            />
                            <Button
                                variant="outline"
                                className="rounded-sm"
                                disabled={importingDates}
                                onClick={() =>
                                    document.getElementById("import-dates-input").click()
                                }
                                data-testid="import-dates-btn"
                            >
                                <UploadSimple size={14} className="mr-2" />
                                {importingDates ? "Importando…" : "Importar fechas (Excel)"}
                            </Button>
                        </div>
                        <ProjectGantt projects={projects} />
                    </TabsContent>
                    <TabsContent value="matrix">
                        <AssignmentsMatrix />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function PlannedByGroupEditor({ groups, value, total, budget, onChange }) {
    const setGroup = (gid, hh) => {
        const next = { ...value };
        const n = Number(hh);
        if (!hh || Number.isNaN(n) || n <= 0) delete next[gid];
        else next[gid] = n;
        onChange(next);
    };

    const fmtEuro = (n) =>
        (n || 0).toLocaleString("es-ES", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }) + " €";

    return (
        <div
            className="border border-slate-200 rounded-sm p-3 bg-slate-50/40"
            data-testid="planned-by-group"
        >
            <div className="flex items-baseline justify-between">
                <Label className="text-xs font-semibold text-slate-700">
                    Horas planificadas por grupo
                </Label>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                    Total{" "}
                    <span
                        className="text-slate-900 font-bold"
                        data-testid="planned-total"
                    >
                        {Number(total || 0).toLocaleString("es-ES", {
                            maximumFractionDigits: 1,
                        })}{" "}
                        h
                    </span>
                </div>
            </div>
            {groups.length === 0 ? (
                <div className="text-xs text-slate-500 py-2">
                    Aún no hay grupos definidos. Créalos desde Usuarios → Grupos y tarifas.
                </div>
            ) : (
                <div className="mt-2 space-y-2">
                    {groups.map((g) => {
                        const h = Number(value[g.group_id] || 0);
                        const subtotal = h * Number(g.hourly_rate || 0);
                        return (
                            <div key={g.group_id} className="space-y-1">
                                <div className="grid grid-cols-[1fr_100px] items-center gap-2">
                                    <div className="text-xs text-slate-700">
                                        <span className="font-medium">{g.name}</span>
                                        <span className="font-mono text-slate-400 ml-1">
                                            · {g.hourly_rate.toFixed(2)} €/h
                                        </span>
                                    </div>
                                    <Input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        placeholder="0"
                                        value={value[g.group_id] ?? ""}
                                        onChange={(e) =>
                                            setGroup(g.group_id, e.target.value)
                                        }
                                        className="rounded-sm border-slate-300 font-mono text-right h-8"
                                        data-testid={`planned-group-${g.group_id}`}
                                    />
                                </div>
                                {h > 0 && (
                                    <div
                                        className="text-right font-mono text-[10px] text-slate-500 pr-1"
                                        data-testid={`planned-group-subtotal-${g.group_id}`}
                                    >
                                        {h.toLocaleString("es-ES", {
                                            maximumFractionDigits: 1,
                                        })}{" "}
                                        h × {g.hourly_rate.toFixed(2)} € ={" "}
                                        <span className="text-slate-800 font-semibold">
                                            {fmtEuro(subtotal)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div
                        className="mt-3 pt-3 border-t border-slate-300 flex items-baseline justify-between"
                        data-testid="planned-budget-total-row"
                    >
                        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-semibold">
                            Presupuesto estimado
                        </span>
                        <span
                            className="font-mono text-base font-bold text-[#002BAF]"
                            data-testid="planned-budget-total"
                        >
                            {fmtEuro(budget)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
